import { generateLegalMoves } from '../movegen/generate.js'
import { isInCheck } from '../movegen/attacks.js'
import {
  KING,
  PAWN,
  typeFromLetter,
  typeLetter,
  type PieceType,
  type PromotionPiece,
} from '../primitives/piece.js'
import {
  FILE_NAMES,
  RANK_NAMES,
  fileOf,
  parseSquare,
  rankOf,
  squareName,
  type FileName,
  type RankName,
  type Square,
} from '../primitives/square.js'
import { isCapture, type Move } from '../position/move.js'
import { makeMove, type Position } from '../position/position.js'
import { err, ok, type Result } from '../result.js'

export type SanError =
  | { readonly kind: 'malformed'; readonly text: string }
  | { readonly kind: 'illegal'; readonly text: string }
  | { readonly kind: 'ambiguous'; readonly text: string; readonly candidates: readonly string[] }

export const describeSanError = (error: SanError): string => {
  switch (error.kind) {
    case 'malformed':
      return `${JSON.stringify(error.text)} is not move notation`
    case 'illegal':
      return `${JSON.stringify(error.text)} is not a legal move here`
    case 'ambiguous':
      return `${JSON.stringify(error.text)} could mean ${error.candidates.join(' or ')}`
  }
}

/** The piece type a move puts in motion. */
const movingPiece = (move: Move): PieceType => {
  switch (move.kind) {
    case 'normal':
      return move.piece
    case 'doublePush':
    case 'enPassant':
    case 'promotion':
      return PAWN
    case 'castle':
      return KING
  }
}

/**
 * The shortest prefix that separates this move from its rivals.
 *
 * Rivals are drawn from the *legal* move list, which is what makes the
 * classic case work: if the only other knight that could reach the square is
 * pinned, its move is not legal, so no disambiguation is written at all.
 */
const disambiguate = (move: Move, legal: readonly Move[]): string => {
  const piece = movingPiece(move)
  const rivals = legal.filter(
    (other) => other.to === move.to && other.from !== move.from && movingPiece(other) === piece,
  )
  if (rivals.length === 0) return ''

  const file = fileOf(move.from)
  if (!rivals.some((other) => fileOf(other.from) === file)) return FILE_NAMES[file] as FileName

  const rank = rankOf(move.from)
  if (!rivals.some((other) => rankOf(other.from) === rank)) return RANK_NAMES[rank] as RankName

  return squareName(move.from)
}

const checkSuffix = (pos: Position, move: Move): string => {
  const after = makeMove(pos, move)
  if (!isInCheck(after)) return ''
  return generateLegalMoves(after).length === 0 ? '#' : '+'
}

/**
 * Render a move in standard algebraic notation.
 *
 * Pass `legal` when serialising a whole game — it is the legal move list for
 * `pos`, and reusing it avoids regenerating moves once per ply.
 */
export const moveToSan = (pos: Position, move: Move, legal?: readonly Move[]): string => {
  if (move.kind === 'castle') {
    return (move.side === 'king' ? 'O-O' : 'O-O-O') + checkSuffix(pos, move)
  }

  const moves = legal ?? generateLegalMoves(pos)
  const piece = movingPiece(move)
  const destination = squareName(move.to)

  let text: string
  if (piece === PAWN) {
    // A pawn capture names the file it left; a pawn push names nothing.
    text = isCapture(move) ? `${FILE_NAMES[fileOf(move.from)] as FileName}x${destination}` : destination
    if (move.kind === 'promotion') text += `=${typeLetter(move.promoteTo)}`
  } else {
    text = `${typeLetter(piece)}${disambiguate(move, moves)}${isCapture(move) ? 'x' : ''}${destination}`
  }

  return text + checkSuffix(pos, move)
}

/**
 * Trailing check, mate, and annotation marks carry no information about which
 * move was played, so they are stripped before matching. `!?`, `?!`, `‼` and
 * friends all fall away here.
 */
const stripDecoration = (text: string): string =>
  text
    .replace(/[+#]+/g, '')
    .replace(/[!?‼⁇⁈⁉]+$/u, '')
    .replace(/\s+/g, '')

const CASTLE_KINGSIDE = /^([0O])-\1$/
const CASTLE_QUEENSIDE = /^([0O])-\1-\1$/

// Piece, optional from-file, optional from-rank, optional capture marker,
// destination, optional promotion in any of the three spellings people use.
const SAN_PATTERN = /^([NBRQK])?([a-h])?([1-8])?(x)?([a-h][1-8])(?:=?([NBRQ])|\(([NBRQ])\))?$/

export const parseSan = (pos: Position, text: string): Result<Move, SanError> => {
  const cleaned = stripDecoration(text)
  const legal = generateLegalMoves(pos)

  // Zeros and letter O are both in the wild, sometimes in the same file.
  if (CASTLE_KINGSIDE.test(cleaned) || CASTLE_QUEENSIDE.test(cleaned)) {
    const side = CASTLE_QUEENSIDE.test(cleaned) ? 'queen' : 'king'
    const castle = legal.find((move) => move.kind === 'castle' && move.side === side)
    return castle === undefined ? err({ kind: 'illegal', text }) : ok(castle)
  }

  const match = SAN_PATTERN.exec(cleaned)
  if (match === null) return err({ kind: 'malformed', text })

  const [, pieceLetterText, fromFileText, fromRankText, , destinationText, promotionA, promotionB] = match
  const piece = pieceLetterText === undefined ? PAWN : (typeFromLetter(pieceLetterText) as PieceType)
  const to = parseSquare(destinationText as string) as Square
  const promotionText = promotionA ?? promotionB
  const promoteTo =
    promotionText === undefined ? null : (typeFromLetter(promotionText) as PromotionPiece)

  const candidates = legal.filter((move) => {
    if (move.to !== to || movingPiece(move) !== piece) return false
    if (fromFileText !== undefined && FILE_NAMES[fileOf(move.from)] !== fromFileText) return false
    if (fromRankText !== undefined && RANK_NAMES[rankOf(move.from)] !== fromRankText) return false
    if (promoteTo === null) return move.kind !== 'promotion'
    return move.kind === 'promotion' && move.promoteTo === promoteTo
  })

  if (candidates.length === 1) return ok(candidates[0] as Move)
  if (candidates.length === 0) return err({ kind: 'illegal', text })
  return err({
    kind: 'ambiguous',
    text,
    candidates: candidates.map((move) => moveToSan(pos, move, legal)),
  })
}
